import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Typography, 
  TextField, 
  Grid, 
  InputAdornment, 
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { RegistrationService } from '../api/generated/requests/services.gen';
import { RegistrationCompleteDTO } from '../api/generated/requests/types.gen';
import { AnalyticsService } from '../services/analyticsService';

// Rozszerzony interfejs RegistrationCompleteDTO
interface ExtendedRegistrationDTO extends RegistrationCompleteDTO {
  confirmPassword: string;
}

// Schemat walidacji formularza
const RegistrationSchema = Yup.object().shape({
  login: Yup.string()
    .min(2, 'Login musi mieć co najmniej 2 znaki')
    .required('Login jest wymagany'),
  password: Yup.string()
    .min(8, 'Hasło musi mieć co najmniej 8 znaków')
    .matches(/[a-z]/, 'Hasło musi zawierać co najmniej jedną małą literę')
    .matches(/[A-Z]/, 'Hasło musi zawierać co najmniej jedną wielką literę')
    .matches(/[0-9]/, 'Hasło musi zawierać co najmniej jedną cyfrę')
    .matches(/[^a-zA-Z0-9]/, 'Hasło musi zawierać co najmniej jeden znak specjalny')
    .required('Hasło jest wymagane'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Hasła muszą być takie same')
    .required('Potwierdzenie hasła jest wymagane'),
  firstName: Yup.string()
    .min(2, 'Imię musi mieć co najmniej 2 znaki')
    .required('Imię jest wymagane'),
  lastName: Yup.string()
    .min(2, 'Nazwisko musi mieć co najmniej 2 znaki')
    .required('Nazwisko jest wymagane')
});

const ConfirmRegisterPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [status, setStatus] = useState<'loading' | 'verifying' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const logoSvg = useMemo(() => (
    <svg
      width="56"
      height="56"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="ustalone-logo-grad" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
          <stop stopColor="#06b6d4" />
          <stop offset="1" stopColor="#2563eb" />
        </linearGradient>
      </defs>
      <circle cx="20" cy="20" r="18" fill="url(#ustalone-logo-grad)" />
      <path
        d="M14 21.5L19 26L27 16"
        stroke="white"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ), []);

  // Funkcja obsługująca przełączanie widoczności hasła
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Funkcja obsługująca przesłanie formularza
  const handleSubmitRegistration = async (values: RegistrationCompleteDTO, { setSubmitting }: FormikHelpers<ExtendedRegistrationDTO>) => {
    if (!token) {
      setStatus('error');
      setMessage('Brak tokenu potwierdzającego. Sprawdź czy link aktywacyjny jest poprawny.');
      setSubmitting(false);
      return;
    }

    // Track registration button click event with form data (excluding passwords)
    await AnalyticsService.trackRegistrationClick({
      login: values.login,
      firstName: values.firstName,
      lastName: values.lastName
    });

    try {
      const response = await RegistrationService.postApiAuthRegisterConfirm({ 
        requestBody: values, 
        token: token
      });
      setStatus('success');
      setMessage('Konto zostało pomyślnie aktywowane. Możesz się teraz zalogować.');
    } catch (error: any) {
      setStatus('error');

      // Check if it's a 400 error with specific backend error format
      if (error?.status === 400 && error?.body) {
        const errorBody = error.body;
        if (errorBody.errorStatus === 400 && errorBody.errorMessage) {
          // Display the specific backend error message with admin contact
          setMessage(`${errorBody.errorMessage}\n\nJeśli uważasz, że to błąd, skontaktuj się z administratorem: admin@ustalone.com`);
        } else {
          // Fallback for other 400 errors
          setMessage('Aktywacja konta nie powiodła się. Spróbuj ponownie później lub skontaktuj się z administratorem: admin@ustalone.com');
        }
      } else {
        // Default error message for other types of errors
        setMessage('Aktywacja konta nie powiodła się. Spróbuj ponownie później lub skontaktuj się z administratorem: admin@ustalone.com');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Sprawdzenie tokenu przy pierwszym renderowaniu
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Brak tokenu potwierdzającego. Sprawdź czy link aktywacyjny jest poprawny.');
      return;
    }
    // Tylko weryfikujemy, że token istnieje i ustawiamy status na verifying,
    // aby pokazać formularz do uzupełnienia danych
    setStatus('verifying');
  }, [token]);

  const handleGoToLogin = () => {
    navigate('/login');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        minHeight: '100vh',
      }}
    >
      {/* LEFT COLUMN */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          px: { xs: 4, md: 8 },
          py: 6,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 480 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            {logoSvg}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                fontSize: '1.5rem',
                color: 'primary.main',
              }}
            >
              ustalone
            </Typography>
          </Box>

          {status === 'loading' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress />
              <Typography variant="h6">Trwa weryfikacja tokenu aktywacyjnego...</Typography>
            </Box>
          ) : status === 'verifying' ? (
            <Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ lineHeight: 3 }}>
                Uzupełnij dane rejestracyjne
              </Typography>

              <Typography variant="body1" sx={{ mb: 4 }}>
                <strong>Ustalone</strong> to <strong>nowoczesny system CRM</strong>, który pomaga zarządzać relacjami z klientami. Aby dokończyć proces rejestracji, wypełnij poniższy formularz.
              </Typography>

              <Formik
                initialValues={{
                  login: '',
                  password: '',
                  confirmPassword: '',
                  firstName: '',
                  lastName: ''
                }}
                validationSchema={RegistrationSchema}
                onSubmit={(values, formikHelpers) => {
                  // Usunięcie pola confirmPassword przed wysłaniem do API
                  const { confirmPassword, ...registrationData } = values;
                  handleSubmitRegistration(registrationData, formikHelpers);
                }}
              >
                {({ errors, touched, isSubmitting, handleChange, handleBlur, values }) => (
                  <Form>
                    <TextField
                      fullWidth
                      id="login"
                      name="login"
                      label="Nazwa użytkownika (login)"
                      value={values.login}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.login && Boolean(errors.login)}
                      helperText={touched.login && errors.login}
                      variant="standard"
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      id="password"
                      name="password"
                      label="Hasło"
                      type={showPassword ? 'text' : 'password'}
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      variant="standard"
                      sx={{ mb: 2 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleTogglePasswordVisibility}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      fullWidth
                      id="confirmPassword"
                      name="confirmPassword"
                      label="Powtórz hasło"
                      type={showPassword ? 'text' : 'password'}
                      value={values.confirmPassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                      helperText={touched.confirmPassword && errors.confirmPassword}
                      variant="standard"
                      sx={{ mb: 2 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleTogglePasswordVisibility}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          id="firstName"
                          name="firstName"
                          label="Imię"
                          value={values.firstName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.firstName && Boolean(errors.firstName)}
                          helperText={touched.firstName && errors.firstName}
                          variant="standard"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          id="lastName"
                          name="lastName"
                          label="Nazwisko"
                          value={values.lastName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.lastName && Boolean(errors.lastName)}
                          helperText={touched.lastName && errors.lastName}
                          variant="standard"
                        />
                      </Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Aktywacja...' : 'Aktywuj konto'}
                      </Button>
                    </Box>
                  </Form>
                )}
              </Formik>
            </Box>
          ) : status === 'success' ? (
            <Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ lineHeight: 5 }}>
                Aktywacja konta zakończona pomyślnie!
              </Typography>
              <Typography variant="body1" sx={{ mb: 4 }}>
                {message}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={handleGoToLogin}>
                  Przejdź do logowania
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ lineHeight: 5 }}>
                Aktywacja konta nie powiodła się
              </Typography>
              <Typography variant="body1" sx={{ mb: 4 }}>
                {message}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="outlined" onClick={() => navigate('/')}>
                  Wróć do strony głównej
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary">
          Copyright © 2025 Devlab Sp. z o.o.
        </Typography>
      </Box>

      {/* RIGHT COLUMN - Tylko obrazek */}
      {!isMobile && (
        <Box
          sx={{
            flex: 1,
            backgroundImage: 'url(/crm-login.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}
    </Box>
  );
};

export default ConfirmRegisterPage;
